import React, {
    useEffect,
    useState
} from "react";

import {
    Navigate,
    useNavigate
} from "react-router-dom";

import {
    useAuth
} from "../context/AuthContext";

import "./Login.css";


const Login = () => {

    const navigate = useNavigate();

    const {
        login,
        verifyOtp,
        resendOtp,
        isAuthenticated,
        loading
    } = useAuth();


    const [username, setUsername] =
        useState("");

    const [password, setPassword] =
        useState("");

    const [showPassword, setShowPassword] =
        useState(false);

    const [error, setError] =
        useState("");

    const [submitting, setSubmitting] =
        useState(false);

    const [otpStep, setOtpStep] = useState(false);
    const [challengeId, setChallengeId] = useState("");
    const [otp, setOtp] = useState("");
    const [notice, setNotice] = useState("");


    // =====================================================
    // REDIRECT IF ALREADY LOGGED IN
    // =====================================================

    useEffect(() => {

        if (
            !loading &&
            isAuthenticated
        ) {

            navigate(
                "/dashboard",
                {
                    replace: true
                }
            );

        }

    }, [
        loading,
        isAuthenticated,
        navigate
    ]);


    // =====================================================
    // PASSWORD LOGIN AND OTP VERIFICATION
    // =====================================================

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setNotice("");

        if (otpStep) {
            if (!/^\d{6}$/.test(otp.trim())) {
                setError("Enter the 6-digit OTP sent to your email.");
                return;
            }

            try {
                setSubmitting(true);
                const result = await verifyOtp(challengeId, otp.trim());

                if (result.success) {
                    navigate("/dashboard", { replace: true });
                } else if (result.pendingApproval) {
                    setNotice(result.message || "Your account is awaiting owner approval.");
                    setOtpStep(false);
                    setChallengeId("");
                    setOtp("");
                    setPassword("");
                } else {
                    setError(result.message || "OTP verification failed.");
                }
            } catch (error) {
                console.error("OTP verification error:", error);
                setError("Unable to verify OTP. Please try again.");
            } finally {
                setSubmitting(false);
            }

            return;
        }

        if (!username.trim()) {
            setError("Please enter your username.");
            return;
        }

        if (!password) {
            setError("Please enter your password.");
            return;
        }

        try {
            setSubmitting(true);
            const result = await login(username.trim(), password);

            if (result.success && result.requiresOtp && result.challengeId) {
                setChallengeId(result.challengeId);
                setOtp("");
                setOtpStep(true);
                setNotice(result.message || "Check your email for the 6-digit OTP.");
            } else {
                setError(result.message || "OTP verification is required to complete login.");
            }
        } catch (error) {
            console.error("Login error:", error);
            setError("Unable to log in. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendOtp = async () => {
        setError("");
        setNotice("");

        try {
            setSubmitting(true);
            const result = await resendOtp(challengeId);

            if (result.success) {
                setNotice(result.message || "A new OTP has been sent.");
                setOtp("");
            } else {
                setError(result.message || "Unable to resend OTP.");
            }
        } catch (error) {
            console.error("Resend OTP error:", error);
            setError("Unable to resend OTP. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // =====================================================
    // LOADING
    // =====================================================

    if (loading) {

        return (

            <div className="login-loading">

                <div className="login-spinner"></div>

                <p>
                    Loading ERP...
                </p>

            </div>

        );

    }


    // =====================================================
    // ALREADY AUTHENTICATED
    // =====================================================

    if (isAuthenticated) {

        return (

            <Navigate
                to="/dashboard"
                replace
            />

        );

    }


    // =====================================================
    // LOGIN PAGE
    // =====================================================

    return (

        <div className="login-page">


            {/* =============================================
                BACKGROUND
            ============================================= */}

            <div className="login-background">

                <div className="login-glow glow-one"></div>

                <div className="login-glow glow-two"></div>

            </div>


            {/* =============================================
                MAIN CONTAINER
            ============================================= */}

            <div className="login-container">


                {/* =========================================
                    BRANDING
                ========================================= */}

                <div className="login-brand">


                    <div className="brand-logo">

                        <div className="brand-logo-mark">
                            SP
                        </div>

                    </div>


                    <h1>
                        Bhimashankar Agro
                    </h1>


                    <h2>
                        Coldchain & Processing producer Co. ltd.
                    </h2>


                    <p className="brand-description">
                        Cold Storage Management
                        & ERP System
                    </p>


                    <div className="brand-divider"></div>


                    <div className="brand-info">

                        <p>
                            Gat No. 628, A/p- Pargaon
                            (Peth)
                        </p>

                        <p>
                            Tal. Ambegaon,
                            Dist. Pune - 410 512
                        </p>

                        <p>
                            Mob. 9822974495 /
                            9921366271 /
                            9921252022
                        </p>

                    </div>


                    <div className="brand-features">


                        <div className="brand-feature">

                            <span>
                                ✓
                            </span>

                            <p>
                                Inventory Management
                            </p>

                        </div>


                        <div className="brand-feature">

                            <span>
                                ✓
                            </span>

                            <p>
                                GRN & Delivery Management
                            </p>

                        </div>


                        <div className="brand-feature">

                            <span>
                                ✓
                            </span>

                            <p>
                                Chamber & Lot Tracking
                            </p>

                        </div>


                    </div>

                </div>


                {/* =========================================
                    LOGIN
                ========================================= */}

                <div className="login-card">


                    <div className="login-card-header">


                        <div className="login-icon">
                            <div className="login-icon-dot"></div>
                        </div>


                        <h2>
                            Welcome Back
                        </h2>


                        <p>
                            Sign in to access the
                            ERP dashboard
                        </p>

                    </div>


                    {/* =====================================
                        ERROR
                    ===================================== */}

                    {error && (

                        <div className="login-error">

                            <span>
                                !
                            </span>

                            <p>
                                {error}
                            </p>

                        </div>

                    )}


                    {notice && (
                        <div className="login-security" role="status">
                            <p>{notice}</p>
                        </div>
                    )}

                    {/* =====================================
                        FORM
                    ===================================== */}

                    <form
                        className="login-form"
                        onSubmit={handleSubmit}
                    >


                        {!otpStep && (
                        <>
                        {/* USERNAME */}

                        <div className="form-group">

                            <label htmlFor="username">
                                Username
                            </label>

                            <div className="input-wrapper">

                                <input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(event) =>
                                        setUsername(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter your username"
                                    autoComplete="username"
                                    disabled={submitting}
                                />

                            </div>

                        </div>


                        {/* PASSWORD */}

                        <div className="form-group">

                            <label htmlFor="password">
                                Password
                            </label>

                            <div className="input-wrapper">

                                <input
                                    id="password"
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    value={password}
                                    onChange={(event) =>
                                        setPassword(
                                            event.target.value
                                        )
                                    }
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                    disabled={submitting}
                                />


                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() =>
                                        setShowPassword(
                                            !showPassword
                                        )
                                    }
                                    disabled={submitting}
                                    aria-label={
                                        showPassword
                                            ? "Hide password"
                                            : "Show password"
                                    }
                                >

                                    {showPassword
                                        ? "Hide"
                                        : "Show"}

                                </button>

                            </div>

                        </div>
                        </>
                        )}

                        {otpStep && (
                            <div className="form-group">
                                <label htmlFor="email-otp">Email verification code</label>
                                <div className="input-wrapper">
                                    <input
                                        id="email-otp"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        value={otp}
                                        onChange={(event) =>
                                            setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
                                        }
                                        placeholder="Enter 6-digit OTP"
                                        disabled={submitting}
                                        aria-label="Six-digit email OTP"
                                    />
                                </div>
                                <div className="otp-actions">
                                    <button
                                        type="button"
                                        className="otp-action-button"
                                        onClick={handleResendOtp}
                                        disabled={submitting}
                                    >
                                        Resend OTP
                                    </button>

                                    <button
                                        type="button"
                                        className="otp-action-button"
                                        onClick={() => {
                                            setOtpStep(false);
                                            setChallengeId("");
                                            setOtp("");
                                            setNotice("");
                                            setError("");
                                        }}
                                        disabled={submitting}
                                    >
                                        Back to login
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* LOGIN BUTTON */}

                        <button
                            type="submit"
                            className="login-button"
                            disabled={submitting}
                        >

                            {submitting ? (

                                <>
                                    <span className="button-spinner"></span>
                                    {otpStep ? "Verifying..." : "Signing in..."}
                                </>

                            ) : (

                                <>
                                    {otpStep ? "Verify OTP" : "Sign In"}

                                    <span className="login-arrow">
                                        →
                                    </span>
                                </>

                            )}

                        </button>

                    </form>


                    {/* =====================================
                        SECURITY
                    ===================================== */}

                    <div className="login-security">

                        <span className="security-line"></span>

                        <p>
                            Secure access to
                            Bhimashankar Agro ERP
                        </p>

                        <span className="security-line"></span>

                    </div>

                </div>

            </div>


            {/* =============================================
                FOOTER
            ============================================= */}

            <div className="login-footer">

                © {new Date().getFullYear()}
                {" "}
                Bhimashankar Agro Coldchain &
                Processing Producer Co. Ltd.
                {" "} | ERP System

            </div>

        </div>

    );

};


export default Login;